import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MessageCircle, Phone, ChevronDown, Lock } from "lucide-react";

interface CreateAgentDropdownProps {
  voiceAgentsEnabled: boolean;
  onCreateChatAgent: () => void;
  onCreateVoiceAgent: () => void;
  disabled?: boolean;
  variant?: "default" | "outline" | "ghost";
  hideChatAgents?: boolean;
}

const CreateAgentDropdown = ({
  voiceAgentsEnabled,
  onCreateChatAgent,
  onCreateVoiceAgent,
  disabled = false,
  variant = "default",
  hideChatAgents = false,
}: CreateAgentDropdownProps) => {
  // If chat is hidden, only allow creating voice agents
  if (hideChatAgents) {
    if (!voiceAgentsEnabled) {
      return (
        <Button disabled={true} size="lg" variant={variant}>
          <Lock className="mr-2 h-4 w-4" />
          Agents Unavailable
        </Button>
      );
    }

    return (
      <Button
        onClick={onCreateVoiceAgent}
        disabled={disabled}
        size="lg"
        variant={variant}
      >
        <Phone className="mr-2 h-4 w-4" />
        Create Voice Agent
      </Button>
    );
  }

  // If voice agents are not enabled, show a simple button for chat
  if (!voiceAgentsEnabled) {
    return (
      <Button
        onClick={onCreateChatAgent}
        disabled={disabled}
        size="lg"
        variant={variant}
      >
        <Plus className="mr-2 h-4 w-4" />
        Create Agent
      </Button>
    );
  }

  // If voice agents are enabled and chat is not hidden, show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button disabled={disabled} size="lg" variant={variant}>
          <Plus className="mr-2 h-4 w-4" />
          Create Agent
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onCreateChatAgent}>
          <MessageCircle className="mr-2 h-4 w-4 text-blue-600" />
          <div className="flex flex-col">
            <span className="font-medium">Chat Agent</span>
            <span className="text-xs text-muted-foreground">
              Create a text-based AI agent
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={onCreateVoiceAgent}>
          <Phone className="mr-2 h-4 w-4 text-green-600" />
          <div className="flex flex-col">
            <span className="font-medium">Voice Agent</span>
            <span className="text-xs text-muted-foreground">
              Create a voice-enabled AI agent
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CreateAgentDropdown;
